import React from "react";
import dayjs from "dayjs";
import { DecreaseArrowIcon, IncreaseArrowIcon } from "./Icons";
import "dayjs/locale/hi"
const GOLD_COLUMNS = [
    { key: "date", label: "तारीख", align: "text-left" },
    { key: "carat24", label: "24 कैरेट" },
    { key: "carat22", label: "22 कैरेट" },
    { key: "carat18", label: "18 कैरेट" },
    { key: "carat14", label: "14 कैरेट" },
];

const SILVER_COLUMNS = [
    { key: "date", label: "तारीख", align: "text-left" },
    { key: "oneGram", label: "1 ग्राम" },
    { key: "tenGram", label: "10 ग्राम" },
    { key: "hundredGram", label: "100 ग्राम" },
    { key: "oneKg", label: "1 किलो" },
];

function formatDate(dateStr) {
    const today = dayjs().format("YYYY-MM-DD");
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    const label = dayjs(dateStr).format("DD MMM");
    const label_hn = dayjs(dateStr)
        .locale("hi")
        .format("DD MMMM")
    if (dateStr?.includes(today)) { return <span className="font-bold">आज</span> }
    if (dateStr?.includes(yesterday)) {

        return <><span className="font-bold">{label_hn}</span></>;
    }

    return <><span className="font-bold">{label}</span> ({label_hn})</>;
}

function formatPrice(value) {
    if (value == null) return "-";
    return `₹${Number(value).toLocaleString("en-IN")}`;
}

function getCellTrend(rates, index, key) {
    const current = rates[index]?.[key];
    const previous = rates[index + 1]?.[key];
    if (current == null || previous == null) return null;
    const diff = current - previous;
    return { diff, isUp: diff > 0 };
}

function bgClassToCss(cls, fallback = "#ffffff") {
    if (!cls) return fallback;
    const match = cls.match(/^bg-\[#([0-9a-fA-F]{3,8})\](?:\/(\d{1,3}))?$/);
    if (!match) return fallback;
    let hex = match[1];
    let alpha = match[2] != null ? Number(match[2]) / 100 : 1;
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (hex.length === 8) {
        alpha = parseInt(hex.slice(6, 8), 16) / 255;
        hex = hex.slice(0, 6);
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function extractHex(cls, fallback) {
    if (!cls) return fallback;
    const match = cls.match(/#([0-9a-fA-F]{3,8})/);
    if (!match) return fallback;
    let hex = match[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    return `#${hex.slice(0, 6)}`;
}

function stickyFillStyle(tintColor, extraShadow, borderColor) {
    const shadows = [extraShadow, `inset 0 0 0 0 ${borderColor}`, `inset 0 0 0 9999px ${tintColor}`].filter(Boolean);
    return {
        backgroundColor: "#ffffff",
        boxShadow: shadows.join(", "),
        transform: "translateZ(0)",
    };
}

export const PriceTable = ({ data, type = "gold", colors = {} }) => {
    const [hoveredRow, setHoveredRow] = React.useState(null);
    const columns = type === "gold" ? GOLD_COLUMNS : SILVER_COLUMNS;
    const allRates = data?.rates ?? [];
    const rates = allRates.filter((r) => !r.hidden);

    return (
        <div >

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className={colors.lightBg || "bg-[#fafafa]"}>
                        <tr>
                            {columns.map((col) =>
                                col.key === "date" ? (
                                    <th
                                        key={col.key}
                                        className={`sticky left-0 z-20 px-4 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap text-left will-change-transform`}
                                        style={stickyFillStyle(bgClassToCss(colors.lightBg, "#fafafa"), "2px 0 4px -2px rgba(0,0,0,0.15)", extractHex(colors.border, "#e5e7eb"))}
                                    >
                                        {col.label}
                                    </th>
                                ) : (
                                    <th
                                        key={col.key}
                                        className={`px-4 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap`}
                                    >
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 70px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">{col.label}</span>
                                            <span />
                                        </div>
                                    </th>
                                )
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {rates.map((row, index) => (
                            <tr
                                key={index}
                                onMouseEnter={() => setHoveredRow(index)}
                                onMouseLeave={() => setHoveredRow(null)}
                                className={` ${index == 0 ? type === "gold" ? "bg-[#FFFDF5]" : "bg-[#F8F9FB99]" : ""}  border-b ${colors.border || "border-gray-200"} last:border-none hover:bg-gray-50`}
                            >
                                {columns.map((col) => {
                                    if (col.key === "date") {
                                        const rowTint = index === 0 ? (type === "gold" ? "bg-[#FFFDF5]" : "bg-[#F8F9FB99]") : "bg-white";
                                        const isLastRow = index === rates.length - 1;
                                        const tintColor = hoveredRow === index ? "#f9fafb" : bgClassToCss(rowTint, "#ffffff");
                                        return (
                                            <td
                                                key="date"
                                                className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-xs md:text-sm text-left font-medium will-change-transform"
                                                style={stickyFillStyle(tintColor, "2px 0 4px -2px rgba(0,0,0,0.15)", isLastRow ? "transparent" : extractHex(colors.border, "#e5e7eb"))}
                                            >
                                                {formatDate(row.date, index)}
                                            </td>
                                        );
                                    }

                                    const cellTrend = getCellTrend(allRates, index, col.key);


                                    return (
                                        <td
                                            key={col.key}
                                            className="px-4 py-3 whitespace-nowrap text-xs md:text-sm"
                                        >
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 70px", alignItems: "center", columnGap: "6px" }}>
                                                <span className="text-right">{formatPrice(row[col.key])}</span>
                                                {cellTrend && (
                                                    <span
                                                        className={`inline-flex items-center justify-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold justify-self-start ${cellTrend.diff === 0
                                                            ? (type === "silver" ? "bg-gray-200 text-black" : "bg-yellow-100 text-black")
                                                            : cellTrend.isUp
                                                                ? "bg-green-100 text-green-600"
                                                                : "bg-red-100 text-red-600"
                                                            }`}
                                                        style={{ minWidth: "64px" }}
                                                    >
                                                        {cellTrend.diff === 0 ? null : cellTrend.isUp ? <IncreaseArrowIcon /> : <DecreaseArrowIcon />}
                                                        {cellTrend.isUp ? "+" : "-"}₹{Math.abs(cellTrend.diff)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}

                        {rates.length === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="px-4 py-6 text-center text-sm  "
                                >
                                    कोई डेटा उपलब्ध नहीं है
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>


        </div>
    );
};
